/**
 * Feedback Rating Step
 *
 * Rating and category selection for feedback.
 */

import { useTranslation } from '@tonnie/locale';
import { cn, Label, RadioGroup, RadioGroupItem } from '@tonnie/ui';
import { CATEGORY_OPTIONS, RATING_OPTIONS, type IFeedbackData } from './constants';

interface IFeedbackRatingStepProps {
  feedback: IFeedbackData;
  onRatingSelect: (rating: number) => void;
  onCategorySelect: (category: string) => void;
}

export function FeedbackRatingStep({
  feedback,
  onRatingSelect,
  onCategorySelect,
}: IFeedbackRatingStepProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Rating Section */}
      <div>
        <h3 className="font-semibold text-base mb-1">{t('feedback.ratingQuestion')}</h3>
        <p className="text-sm text-muted-foreground mb-4">{t('feedback.ratingDescription')}</p>
        <div
          className="grid grid-cols-5 gap-2"
          role="radiogroup"
          aria-label={t('feedback.aria.satisfactionRating')}
        >
          {RATING_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => onRatingSelect(option.value)}
                data-testid={`feedback-rating-${option.value}`}
                className={cn(
                  'flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all hover:border-primary/50 hover:bg-primary/5',
                  feedback.rating === option.value
                    ? 'border-primary bg-primary/10'
                    : 'border-sidebar-border'
                )}
                role="radio"
                aria-checked={feedback.rating === option.value}
                aria-label={t('feedback.aria.ratingOption', {
                  label: t(option.labelKey),
                  value: option.value,
                })}
              >
                <Icon className={cn('w-7 h-7', option.color)} aria-hidden="true" />
                <span className="text-2xs text-center text-muted-foreground leading-tight">
                  {t(option.labelKey)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Category Section */}
      <div>
        <h3 className="font-semibold text-base mb-1">{t('feedback.categoryQuestion')}</h3>
        <p className="text-sm text-muted-foreground mb-4">{t('feedback.categoryDescription')}</p>
        <RadioGroup
          value={feedback.category || ''}
          onValueChange={onCategorySelect}
          className="grid grid-cols-1 gap-2"
        >
          {CATEGORY_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <div key={option.value}>
                <RadioGroupItem
                  value={option.value}
                  data-testid={`feedback-category-${option.value}`}
                  id={`category-${option.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`category-${option.value}`}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all hover:border-primary/50 hover:bg-primary/5',
                    feedback.category === option.value
                      ? 'border-primary bg-primary/10'
                      : 'border-sidebar-border'
                  )}
                >
                  <Icon
                    className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{t(option.labelKey)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t(option.descriptionKey)}
                    </div>
                  </div>
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      </div>
    </div>
  );
}
